/** @prettier */
import classNames from 'classnames';
import { List } from 'immutable';
import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';

import AnimatedContent from '@xanadu/ui-shared/components/AnimatedContent';
import SportIcon from '@xanadu/ui-shared/components/SportIcon';
import Highlighted from '@xanadu/ui-shared/components/common/Highlighted';
import Spinner from '@xanadu/ui-shared/components/widgets/Spinner';
import { PRODUCTS } from '@xanadu/ui-shared/constants';
import { useImprovedTranslation } from '@xanadu/ui-shared/hooks';
import { formatEventStartTime } from '@xanadu/ui-shared/utils/dates';
import { injectTimezone } from '@xanadu/ui-shared/utils/events';
import * as utils from '@xanadu/ui-shared/utils/metaTags';

import IconSearch from '../../../../images/icons/misc/icon-search.svg';

import Placeholder from './Placeholder';
import styles from './SearchResults.module.scss';

const SearchResults = ({
  isMobile = false,
  query,
  results,
  loading,
  onSearchResultClick = () => {},
  tags,
  displayTimeZoneOnRace = false,
}) => {
  const { t } = useImprovedTranslation();

  const mainClass = classNames({
    [styles.main]: true,
    [styles['main--noResults']]: !results.size && !loading,
    [styles['main--isMobile']]: isMobile,
  });

  let content = null;

  if (!results.size && !loading) {
    content = (
      <div className={styles.noResults}>
        <IconSearch />
        <span>{t('No events match your search')}</span>
      </div>
    );
  }

  if (!isMobile && loading) {
    content = (
      <React.Fragment>
        <Placeholder styles={styles} />
        <Placeholder styles={styles} />
      </React.Fragment>
    );
  }

  if (isMobile && loading) {
    content = <Spinner theme="dark" size="large" />;
  }

  if (results.size && !loading) {
    content = (
      <AnimatedContent mountDelay={50}>
        <div className={styles.containerRows}>
          <div className={styles.rows}>
            {results.map((event) => {
              const { id, name, startTime, sport, metaTags } = event;

              const { dateStr, timeStr } = formatEventStartTime(new Date(startTime));

              const { name: competitionName } = metaTags.find(
                ({ type }) => type === 'COMPETITION'
              ) || {
                name: sport,
              };

              let injectedName = name;
              if (sport.includes('racing') && displayTimeZoneOnRace) {
                injectedName = injectTimezone(name);
              }

              return (
                <Link
                  key={id}
                  to={utils.getEventURL(event, tags, PRODUCTS.exchange) || ''}
                  className={styles.eventRow}
                  onClick={onSearchResultClick}
                  data-hook="search-result-item"
                >
                  <span>
                    <div className={styles.sportIcon}>
                      <SportIcon sport={sport} size="20px" theme="dark" />
                    </div>
                    <div className={styles.eventInfo}>
                      <Highlighted
                        text={injectedName}
                        highlight={query}
                        highlightClass={styles.highlight}
                      />
                      <span className={styles.eventCompetition}>{competitionName}</span>
                      <span className={styles.eventTime}>
                        {dateStr} {timeStr}
                      </span>
                    </div>
                  </span>
                  <span className={styles.goToEvent}>{t('Go to event')}</span>
                </Link>
              );
            })}
          </div>
          {results.size > 0 && (
            <div className={styles.numberOfResults}>
              {t('count Results', { count: results.size })}
            </div>
          )}
        </div>
      </AnimatedContent>
    );
  }

  return (
    <div className={mainClass}>
      <div className={styles.header}>
        <span>
          <IconSearch />
          <span>{t('Search Results')}</span>
        </span>
      </div>
      {content}
    </div>
  );
};

SearchResults.propTypes = {
  results: PropTypes.instanceOf(List).isRequired,
  loading: PropTypes.bool.isRequired,
  onSearchResultClick: PropTypes.func,
  query: PropTypes.string.isRequired,
  tags: PropTypes.instanceOf(List).isRequired,
  isMobile: PropTypes.bool,
  displayTimeZoneOnRace: PropTypes.bool,
};

function mapStateToProps(state) {
  return {
    tags: state.getIn(['navigation', 'tags']),
    loading: state.getIn(['local', 'search', 'loading']),
    results: state.getIn(['local', 'search', 'results']),
    query: state.getIn(['local', 'search', 'value']),
  };
}

export const Component = SearchResults;

export default connect(mapStateToProps)(SearchResults);
